import el from "@cypherpotato/el";
import page from "page";
import { api, sleep } from "../../../api";
import { Badge, BadgeList } from "../../../components/badge";
import { Button, ButtonRow, DangerButton, PrimaryButton } from "../../../components/button";
import { Card, CardContent, CardGrid, CardHeader } from "../../../components/card";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { Dropdown, DropdownDivider, DropdownItem } from "../../../components/dropdown";
import { FileInput } from "../../../components/file-input";
import { Checkbox, FormControl, Select, TagsInput, TextArea, TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { CenteredContainer, Content, Padding, Row, Section } from "../../../components/layout";
import { ExternalLink, Link } from "../../../components/link";
import { DefaultRow, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "../../../components/table";
import { CopiableText, DashboardTextHeading, Heading, InlineCode, MultilingualCodeWrapper, MutedText, Paragraph } from "../../../components/text";
import { getSession } from "../../../session";
import { displayAlert, displayFormControl } from "../../../ui/alert";
import { showFlyout } from "../../../ui/flyout";
import { runOperation, updateOperationText } from "../../../ui/operation";
import { throttledCall } from "../../../ui/throttle";
import { DateTimeLabel } from "../../../components/datetime-label";
import { showDialog } from "@codemirror/view";
import { formatNumber } from "../../../ui/format";
import { SpecializedControl } from "../../../components/specializedObject";
import { TabControl } from "../../../components/tabcontrol";
import { BigTextPreview } from "../../../components/big-text-preview";
import { CodeEditor } from "../../../components/code-editor";
import { Editor, EditorDescription, EditorRow, EditorTitle } from "../../../components/editor";
import { acceptDiff } from "../../../ui/accept-diff";
import { find, identifierName } from "../../../util";
import { Definition, DefinitionList } from "../../../components/definition";

export default function (ctx) {

    const collectionId = ctx.params.id;
    const documents = el.state.create(null);
    const collection = el.state.create(null);
    const filterName = el.state.create("");
    const filterState = el.state.create(null);
    const orderBy = el.state.create("updated_at_desc");

    const MEDIA_UPLOAD_CHUNK_SIZE_BYTES = 50 * 1024 * 1024;

    async function uploadMediaFileInChunks(file, algorithm, options) {
        const fileIndex = options?.fileIndex ?? 0;
        const totalFiles = options?.totalFiles ?? 1;

        const headers = {
            "X-Import-Media-File-Name": file.name,
            "X-Import-Media-Algorithm": algorithm
        };

        const totalChunks = Math.max(1, Math.ceil(file.size / MEDIA_UPLOAD_CHUNK_SIZE_BYTES));

        updateOperationText(`Importing documents...`, `Uploading ${file.name} (${fileIndex + 1}/${totalFiles})`);

        let chunkIndex = 0;
        for (let offset = 0; offset < file.size; offset += MEDIA_UPLOAD_CHUNK_SIZE_BYTES) {
            const chunk = file.slice(offset, offset + MEDIA_UPLOAD_CHUNK_SIZE_BYTES);

            const nextUploadedBytes = Math.min(file.size, offset + chunk.size);
            const percent = file.size > 0 ? Math.floor((nextUploadedBytes / file.size) * 100) : 0;
            updateOperationText(`Importing documents...`, `Uploading ${file.name} (${fileIndex + 1}/${totalFiles}) - Chunk ${chunkIndex + 1}/${totalChunks} (${percent}%)`);

            const response = await api.fetch(`/api/v1/collections/${collectionId}/documents/media/chunk`, {
                method: 'POST',
                headers,
                body: chunk
            });

            if (!response.ok) {
                await api.parseBody(response);
                throw new Error(`Failed to upload chunk for ${file.name}.`);
            }

            chunkIndex++;
            await sleep(2000);
        }

        updateOperationText(`Importing documents...`, `Finalizing ${file.name} (${fileIndex + 1}/${totalFiles})`);
        const completeResponse = await api.fetch(`/api/v1/collections/${collectionId}/documents/media/complete`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ context: options?.context ?? "" })
        });

        await api.parseBody(completeResponse);

        if (!completeResponse.ok) {
            throw new Error(`Failed to complete upload for ${file.name}.`);
        }
    }

    async function reload() {
        const query = new URLSearchParams({ order_by: orderBy.value });
        if (filterName.value) {
            query.set("filter", filterName.value);
        }
        if (filterState.value) {
            query.set("state", filterState.value);
        }

        const response = await api.getJson(`/api/v1/collections/${collectionId}/documents?` + query);
        documents.value = Array.isArray(response.data) ? response.data : response.data?.items ?? [];
    }

    function reloadCollection() {
        runOperation("Reading collection data...", async () => {
            const response = await api.getJson(`/api/v1/collections/${collectionId}`);
            collection.value = response.data;
            reload();
        });
    }

    const DetailListOptions = {
        style: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '6px 15px'
        }
    };

    reloadCollection();

    return Dashboard(
        {
            classList: ["full-size"]
        },
        Card(
            CardContent(
                Section(
                    Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                        onClick() {
                            history.go(-1);
                            page();
                        }
                    })
                ),
                DashboardTitle(
                    el.state.listening([collection], () => DashboardTextHeading(collection.value?.name, "Collection"))
                )
            ),
            el.state.listening([collection], () =>
                TabControl([
                    {
                        title: "Browse documents",
                        contents: el.state.listening([documents], () =>
                            Card(
                                CardContent(
                                    Section(
                                        el.state.listening([collection], () =>
                                            CardGrid(
                                                {
                                                    classList: ["enforced-small-size"],
                                                },
                                                Card(
                                                    CardHeader("Total documents"),
                                                    CardContent(
                                                        el("h2", { style: { margin: 0, fontWeight: 400 } }, formatNumber(collection.value?.state?.totalDocuments ?? 0))
                                                    )
                                                ),
                                                Card(
                                                    CardHeader("Indexed"),
                                                    CardContent(
                                                        el("h2", { style: { margin: 0, fontWeight: 400 } }, formatNumber(collection.value?.state?.indexedDocuments ?? 0))
                                                    )
                                                ),
                                                Card(
                                                    CardHeader("Queued"),
                                                    CardContent(
                                                        el("h2", { style: { margin: 0, fontWeight: 400 } }, formatNumber(collection.value?.state?.queuedDocuments ?? 0))
                                                    )
                                                ),
                                                Card(
                                                    CardHeader("Outdated"),
                                                    CardContent(
                                                        el("h2", { style: { margin: 0, fontWeight: 400 } }, formatNumber(collection.value?.state?.outdatedDocuments ?? 0))
                                                    )
                                                ),
                                            )
                                        )
                                    ),
                                ),
                                CardContent(
                                    CommandBar(
                                        Button(Icon("add-line"), "New document", {
                                            onClick() {
                                                page(`/dashboard/collections/${collectionId}/documents/create`);
                                            }
                                        }),
                                        Button(Icon("terminal-line"), "Playground", {
                                            onClick() {
                                                page(`/dashboard/collections/${collectionId}/playground`);
                                            }
                                        }),
                                        Button(Icon("share-forward-line"), "Navigation", {
                                            onClick() {
                                                page(`/dashboard/collections/${collectionId}/navigation`);
                                            }
                                        }),
                                        Dropdown("Options",
                                            DropdownItem("View MCP code", Icon("code-line"), {
                                                onClick() {
                                                    displayAlert({
                                                        title: "MCP code",
                                                        dialogSize: "medium",
                                                        message: el.fragment(
                                                            "Use the code below to integrate your AI model as a MCP source.",
                                                            MultilingualCodeWrapper("Example", [
                                                                {
                                                                    label: "VSCode mcp.json",
                                                                    language: "json",
                                                                    code: `
{
    "servers": {
        "${identifierName(collection.value?.name)}": {
            "type": "http",
            "url": "${window.env.apiEndpoint}v1/mcp/collections",
            "headers": {
                "Authorization": "Bearer ${getSession()?.sessionKey}",
                "X-Mcp-Collection-Id": "${collectionId}",
                "X-Mcp-Collection-Name": "${collection.value?.name}",
                "X-Mcp-Top-K": "5",
                "X-Mcp-Min-Score": "0.4",
                "X-Mcp-Use-References": "none", // or "all"
            }
        }
    }
}                                                    
                                                                                                        `
                                                                }
                                                            ])
                                                        )
                                                    })
                                                }
                                            }),
                                            DropdownDivider(),
                                            DropdownItem("Import from files", Icon("image-add-line"), {
                                                onClick() {
                                                    displayFormControl({
                                                        title: "Import from files",
                                                        message: el.fragment(
                                                            Paragraph(
                                                                "Provide a media file to extract documents and information from it.",
                                                                "This file will be processed with an LLM to extract information using OCR. Images, videos, audios, and PDF documents are supported."
                                                            )
                                                        ),
                                                        formControlItem: SpecializedControl({
                                                            fields: [
                                                                {
                                                                    name: "files",
                                                                    label: "Files",
                                                                    control: FileInput({ accept: "image/*,audio/*,video/*,.pdf", multiple: true })
                                                                },
                                                                {
                                                                    name: "algo",
                                                                    label: "Extraction algorithm",
                                                                    control: Select({
                                                                        items: [
                                                                            {
                                                                                text: "Compound Low",
                                                                                value: "CompoundLow"
                                                                            },
                                                                            {
                                                                                text: "Compound High",
                                                                                value: "CompoundHigh"
                                                                            },
                                                                            {
                                                                                text: "Interview",
                                                                                value: "Interview"
                                                                            },
                                                                        ]
                                                                    })
                                                                },
                                                                {
                                                                    name: "context",
                                                                    label: "Context",
                                                                    control: TextArea(null, "")
                                                                },
                                                            ]
                                                        }),
                                                        validate: [
                                                            [x => !!x.files.length, "You must attach a file to continue."]
                                                        ],
                                                        onConfirm(value) {

                                                            runOperation("Importing documents...", async () => {
                                                                const totalFiles = value.files.length;
                                                                for (let i = 0; i < totalFiles; i++) {
                                                                    const file = value.files[i];
                                                                    await uploadMediaFileInChunks(file, value.algo, { fileIndex: i, totalFiles, context: value.context });
                                                                }

                                                                reload();
                                                            });
                                                        }
                                                    });
                                                }
                                            }),
                                            DropdownDivider(),
                                            DropdownItem("Import from JSONL", Icon("download-2-line"), {
                                                onClick() {
                                                    displayFormControl({
                                                        title: "Import document collection",
                                                        message: el.fragment(
                                                            Paragraph(
                                                                "Indicate a list of documents in JSONL format to import into the collection. ",
                                                                "Read the ", ExternalLink("documentation", window.env.apiEndpoint + "apidocs"), " to know how to ",
                                                                "prepare your list of documents for import."
                                                            ),
                                                            Paragraph(
                                                                "Documents that already exist through the name and that have their content unchanged are ignored."
                                                            ),
                                                            Paragraph(
                                                                "The maximum number of documents per file is 10,000 documents and a maximum of 50MB per file."
                                                            )
                                                        ),
                                                        formControlItem: FileInput({ accept: ".jsonl" }),
                                                        validate: [
                                                            [x => x != null, "You must attach a file to continue."]
                                                        ],
                                                        onConfirm(value) {

                                                            runOperation("Importing documents...", async () => {
                                                                const formData = new FormData();
                                                                formData.append("documents", value);

                                                                const response = await api.fetch(`/api/v1/collections/${collectionId}/documents`, {
                                                                    method: 'POST',
                                                                    body: formData
                                                                });
                                                                await api.parseBody(response);

                                                                reload();
                                                            });
                                                        }
                                                    });
                                                }
                                            }),
                                            DropdownItem("Export to JSONL", Icon("upload-2-line"), {
                                                onClick() {
                                                    var sessionKey = getSession().sessionKey;
                                                    window.open(window.env.apiEndpoint + `/api/v1/collections/${collectionId}/export.jsonl?api-key=${sessionKey}`, "_blank");
                                                }
                                            })
                                        )
                                    )
                                ),
                                CardContent(
                                    CommandBar(
                                        FormControl("Search",
                                            Row({ formSlot: true },
                                                TextInput("searchInput", "Name, ID or content", {
                                                    onInput(event) {
                                                        throttledCall(() => {
                                                            filterName.value = event.target.value;
                                                            reload();
                                                        }, 300);
                                                    },
                                                    value: filterName.value
                                                }),
                                                el.state.listening([collection], () =>
                                                    Button(Icon("price-tag-3-line"), {
                                                        onClick() {
                                                            showFlyout(
                                                                Paragraph("Click on a tag to filter documents by that tag"),
                                                                el.state.listening([filterName, collection], () =>
                                                                    Paragraph(
                                                                        BadgeList(
                                                                            {
                                                                                style: {
                                                                                    maxWidth: "400px"
                                                                                }
                                                                            },
                                                                            collection.value?.tags.map(tag =>
                                                                                Badge(tag, null, {
                                                                                    onClick(event) {
                                                                                        const input = document.querySelector("#searchInput");
                                                                                        if (event.target.dataset.isChecked) {
                                                                                            input.value = input.value.replace(`-t "${tag}"`, "");
                                                                                        } else {
                                                                                            input.value += `-t "${tag}"`
                                                                                        }

                                                                                        filterName.value = input.value;
                                                                                        reload();
                                                                                    },
                                                                                    dataIsChecked: filterName.value.includes(`"${tag}"`),
                                                                                    style: {
                                                                                        opacity: filterName.value.includes(`"${tag}"`) ? "1" : ".4",
                                                                                        cursor: "pointer"
                                                                                    }
                                                                                })
                                                                            )
                                                                        )
                                                                    )
                                                                )
                                                            )
                                                        }
                                                    })
                                                ),
                                                Button(Icon("question-line"), {
                                                    onClick() {
                                                        showFlyout(
                                                            Paragraph("Search documents using a search expression:"),
                                                            el("ul",
                                                                el("li",
                                                                    InlineCode("-t tag1 tag2"),
                                                                    " - documents that contain one of the specified tags."
                                                                ),
                                                                el("li",
                                                                    InlineCode("-c \"part of the contents\""),
                                                                    " - documents that contain a part of the specified content."
                                                                ),
                                                                el("li",
                                                                    InlineCode("-n \"part of the name\""),
                                                                    " - documents that contain a part of the specified name."
                                                                ),
                                                                el("li",
                                                                    InlineCode("-r \"reference\""),
                                                                    " - documents that are from the specified reference."
                                                                ),
                                                                el("li",
                                                                    InlineCode("-i \"document id\""),
                                                                    " - finds a document by the specified ID."
                                                                )
                                                            )
                                                        )
                                                    }
                                                })
                                            )
                                        ),
                                        FormControl("Filter state",
                                            Select({
                                                value: null,
                                                items: [
                                                    { value: "", text: "All states" },
                                                    { value: "indexed", text: "Indexed" },
                                                    { value: "queued", text: "Queued" },
                                                ],
                                                onChange(event) {
                                                    filterState.value = event.target.value;
                                                    reload();
                                                }
                                            })
                                        ),
                                        FormControl("Order by",
                                            Select({
                                                value: "updated_at_desc",
                                                items: [
                                                    { value: "created_at_asce", text: "Created-at (A-Z)" },
                                                    { value: "created_at_desc", text: "Created-at (Z-A)" },
                                                    { value: "updated_at_asce", text: "Updated-at (A-Z)" },
                                                    { value: "updated_at_desc", text: "Updated-at (Z-A)" },
                                                    { value: "indexed_at_asce", text: "Indexed-at (A-Z)" },
                                                    { value: "indexed_at_desc", text: "Indexed-at (Z-A)" },
                                                ],
                                                onChange(event) {
                                                    orderBy.value = event.target.value;
                                                    reload();
                                                }
                                            })
                                        ),
                                    )
                                ),
                                CardContent(
                                    Table(
                                        {
                                            pagination: {
                                                perPage: 100
                                            }
                                        },
                                        TableHeader(
                                            TableHeaderCell("Id."),
                                            TableHeaderCell("Updated"),
                                            TableHeaderCell("Name"),
                                            TableHeaderCell("Ref. Id"),
                                            TableHeaderCell("Tags"),
                                            TableHeaderCell("Contents"),
                                            TableHeaderCell("State"),
                                            TableHeaderCell("Actions")
                                        ),
                                        el.state.listening([documents], () =>
                                            TableBody(
                                                documents.value?.map(item =>
                                                    TableRow(
                                                        TableCell(
                                                            CopiableText('#' + item.id.substr(-4), item.id)
                                                        ),
                                                        TableCell(
                                                            DateTimeLabel(item.updatedAt)
                                                        ),
                                                        TableCell(
                                                            CopiableText(item.name)
                                                        ),
                                                        TableCell(
                                                            (item.reference && CopiableText(item.reference)) ?? MutedText("None")
                                                        ),
                                                        TableCell(
                                                            BadgeList(
                                                                ...item.tags.map(tag => Badge(tag))
                                                            )
                                                        ),
                                                        TableCell(
                                                            item.contentsPreview
                                                        ),
                                                        TableCell(
                                                            Badge(item.indexState[0])
                                                        ),
                                                        TableCell(
                                                            ButtonRow(
                                                                Button(Icon("pencil-line"), "Edit", {
                                                                    onClick() {
                                                                        page(`/dashboard/collections/${collectionId}/documents/${item.id}`);
                                                                    }
                                                                }),
                                                                Dropdown(null,
                                                                    DropdownItem("Details", Icon("file-list-3-line"), {
                                                                        onClick() {
                                                                            runOperation("Loading document details...", async () => {
                                                                                const response = await api.getJson(`/api/v1/collections/${collectionId}/documents/${item.id}`);
                                                                                const doc = response.data;

                                                                                displayAlert({
                                                                                    title: "Document details",
                                                                                    message: el.fragment(
                                                                                        Card(
                                                                                            CardHeader("Document information"),
                                                                                            CardContent(
                                                                                                DefinitionList(
                                                                                                    DetailListOptions,
                                                                                                    Definition("ID", CopiableText(doc.id)),
                                                                                                    Definition("Name", CopiableText(doc.name)),
                                                                                                    Definition("State", doc.state[0]),
                                                                                                    Definition("Reference ID", doc.reference && CopiableText(doc.reference) || MutedText("-")),
                                                                                                    Definition("Tags", BadgeList(...doc.tags.map(t => Badge(t)))),
                                                                                                )
                                                                                            )
                                                                                        ),
                                                                                        Card(
                                                                                            CardHeader("Timestamps"),
                                                                                            CardContent(
                                                                                                DefinitionList(
                                                                                                    DetailListOptions,
                                                                                                    Definition("Created at", DateTimeLabel(doc.createdAt)),
                                                                                                    Definition("Updated at", DateTimeLabel(doc.updatedAt)),
                                                                                                    Definition("Indexed at", doc.indexedAt && DateTimeLabel(doc.indexedAt) || MutedText("-")),
                                                                                                )
                                                                                            )
                                                                                        ),
                                                                                        Card(
                                                                                            CardHeader("Content information"),
                                                                                            CardContent(
                                                                                                DefinitionList(
                                                                                                    DetailListOptions,
                                                                                                    Definition("Total characters", formatNumber(doc.characterCount)),
                                                                                                    Definition("Total words", formatNumber(doc.wordCount)),
                                                                                                    Definition("Aprx. token count", formatNumber(doc.approximateTokenCount)),
                                                                                                )
                                                                                            )
                                                                                        )
                                                                                    )
                                                                                })
                                                                            });
                                                                        }
                                                                    }),
                                                                    DropdownItem("Delete", Icon("delete-bin-line"), {
                                                                        onClick() {
                                                                            showFlyout(
                                                                                Paragraph("Are you sure you want to delete this document? This action cannot be undone."),
                                                                                item.reference && Paragraph(
                                                                                    Checkbox(null, "Include documents with same reference")
                                                                                ),
                                                                                DangerButton("Yes, delete document",
                                                                                    {
                                                                                        onClick(event) {
                                                                                            runOperation("Deleting document...", async () => {

                                                                                                var includeReferences = event.target.closest(".flyout").querySelector("input[type=checkbox]");
                                                                                                console.log(includeReferences)
                                                                                                if (includeReferences) {
                                                                                                    await api.deleteJson(`/api/v1/collections/${collectionId}/documents/${item.id}?include-references=true`);
                                                                                                } else {
                                                                                                    await api.deleteJson(`/api/v1/collections/${collectionId}/documents/${item.id}`);
                                                                                                }
                                                                                                reload();
                                                                                            });
                                                                                        }
                                                                                    }
                                                                                )
                                                                            )
                                                                        }
                                                                    })
                                                                )
                                                            )
                                                        )
                                                    )
                                                ).nullWhenEmpty() || DefaultRow("No documents found"),
                                                {
                                                    mount() {
                                                        this.closest('table').refreshPagination();
                                                    }
                                                }
                                            )
                                        )
                                    )
                                )
                            ))
                    },
                    {
                        title: "Settings",
                        contents: CenteredContainer(
                            Heading(
                                "Collection settings",
                                "Set up the basic configuration for your collection."
                            ),
                            Section(
                                CommandBar(
                                    PrimaryButton("Save changes",
                                        {
                                            onClick() {
                                                runOperation("Saving collection...", async () => {
                                                    await api.putJson(`/api/v1/collections/${collectionId}`, {
                                                        collectionName: find("collectionName").value,
                                                        context: find("collectionContext").value?.nullWhenEmpty(),
                                                        tags: find("collectionContextualTags").value.join(",")
                                                    });
                                                    reloadCollection();
                                                });
                                            }
                                        }
                                    ),
                                    Dropdown("Advanced settings",
                                        DropdownItem("Update outdated documents", Icon("arrow-up-circle-line"), {
                                            onClick() {
                                                showFlyout(
                                                    Paragraph("This will update outdated document vectors."),
                                                    Paragraph("Do you want to continue?"),
                                                    PrimaryButton("Yes, update vectors", {
                                                        onClick() {
                                                            runOperation("Updating collection...", async () => {
                                                                await api.deleteJson(`/api/v1/collections/${collectionId}/vectors-only`);
                                                                reloadCollection();
                                                            });
                                                        }
                                                    })
                                                );
                                            }
                                        }),
                                        DropdownDivider(),
                                        DropdownItem("Reset collection", Icon("reset-left-line"), {
                                            onClick() {
                                                showFlyout(
                                                    Paragraph("Are you sure you want to reset this collection?"),
                                                    Paragraph("This action will delete all pending and indexed documents, but not the collection itself."),
                                                    DangerButton(Icon("reset-left-line"), "Reset collection", {
                                                        onClick() {
                                                            runOperation("Resetting collection...", async () => {
                                                                await api.deleteJson(`/api/v1/collections/${collectionId}/reset-only`);
                                                                reloadCollection();
                                                            });
                                                        }
                                                    })
                                                );
                                            }
                                        }),
                                        DropdownItem("Delete collection", Icon("delete-bin-line"), {
                                            onClick() {
                                                showFlyout(
                                                    Paragraph("Are you sure you want to delete this collection?"),
                                                    Paragraph("This action cannot be undone and all documents will be deleted."),
                                                    DangerButton(Icon("delete-bin-line"), "Delete collection", {
                                                        onClick() {
                                                            runOperation("Deleting collection...", async () => {
                                                                await api.deleteJson(`/api/v1/collections/${collectionId}`);
                                                                page("/dashboard/collections");
                                                            });
                                                        }
                                                    })
                                                );
                                            }
                                        })
                                    )
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "collectionName",
                                        value: collection.value?.name,
                                        renderValue(value) {
                                            return Button(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Collection name", { value }),
                                                onConfirm: callback,
                                                validate: [[x => x.length > 3, "Name must be at least 3 characters long"]]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Collection Name"),
                                        Paragraph("The collection name is used to identify the collection within the system and in interactions with the LLM."),
                                    )
                                ),
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "collectionContext",
                                        value: collection.value?.context,
                                        renderValue(value) {
                                            return BigTextPreview(value)
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Collection Context"),
                                        Paragraph("Provide a hint about what the collection contains in its documents, to give context and \"knowledge awareness\" about the collection to the LLM that uses it."),
                                    )
                                ),
                                EditorRow(
                                    PrimaryButton(Icon("quill-pen-ai-line"), "Generate context from documents", {
                                        onClick() {
                                            showFlyout(
                                                Paragraph("This feature generates a general context for the collection based on the content of the documents already present in the collection."),
                                                Paragraph("This operation uses the configured processing model for this server."),
                                                Paragraph("Do you want to continue?"),
                                                PrimaryButton("Generate context", {
                                                    onClick() {
                                                        runOperation("Generating context...", async () => {
                                                            const result = await api.postJson(`/api/v1/collections/${collectionId}/generate-context`);

                                                            acceptDiff(find("collectionContext").value, result.data.context).then((acceptedResult) => {
                                                                find("collectionContext").value = acceptedResult;
                                                                find("collectionContextualTags").value = result.data.tags.split(',').map(t => t.trim());
                                                            });
                                                        });
                                                    }
                                                })
                                            )
                                        }
                                    })
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "collectionContextualTags",
                                        value: collection.value?.contextualTags,
                                        renderValue(value) {
                                            return Button(
                                                BadgeList(
                                                    value?.length > 0 ? value?.slice(0, 4).map(tag => Badge(tag)) : MutedText("No tags")
                                                )
                                            )
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TagsInput("tags", { value }),
                                                onConfirm: callback
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Collection Tags"),
                                        Paragraph("Tags are used to categorize and organize the collection as a whole."),
                                    )
                                ),
                            )
                        )
                    }
                ], {
                    tabstripExtraPadded: true
                })
            )
        )
    );
}
